"use client";

// Scroll-gesteuerte Stack-Animation, cinematic full-bleed Variante.
// Frame nimmt fast die volle Breite ein, Text liegt als Overlay im unteren Drittel.
// Jeder neue Screenshot fährt von unten ins Bild und stapelt sich auf dem vorherigen.

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

type Shot = {
  src: string;
  url: string;
  title: string;
  body: string;
};

export function MockupsStack({ shots }: { shots: Shot[] }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = -rect.top;
      const p = total > 0 ? Math.max(0, Math.min(1, scrolled / total)) : 0;
      setProgress(p);
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  const N = shots.length;
  const segment = 1 / (N - 1);

  const slideProgress = (i: number) => {
    if (i === 0) return 1;
    const start = (i - 1) * segment;
    return Math.max(0, Math.min(1, (progress - start) / segment));
  };

  const activeIndex = Math.min(N - 1, Math.round(progress * (N - 1)));
  const active = shots[activeIndex];

  return (
    <section
      ref={sectionRef}
      className="relative"
      // Ziel: ~2× Viewport-Höhe gesamt für alle N Slides zusammen.
      // (N-1) × 40dvh pro Übergang + 80dvh für Einblick = ~200dvh bei N=4 ≈ 1.800px auf 900px-Viewport.
      style={{ height: `${(N - 1) * 40 + 80}dvh` }}
    >
      <div className="sticky top-0 h-[100dvh] flex flex-col items-center justify-center gap-8 overflow-hidden py-8">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div
            className="relative w-full mx-auto rounded-[18px] border border-[var(--color-line)] bg-white overflow-hidden"
            style={{
              maxWidth: 1400,
              aspectRatio: "1680 / 1050",
              maxHeight: "calc(100dvh - 260px)",
              boxShadow:
                "0 60px 120px -30px rgba(10,10,15,0.22), 0 0 0 1px rgba(10,10,15,0.04)",
            }}
          >
            {/* Browser-Bar — fixe Höhe, bleibt immer oben, hat höheren Stacking-Context als der Image-Stack */}
            <div
              className="absolute inset-x-0 top-0 z-30 flex items-center gap-2 px-5 bg-[#F4F4F0] border-b border-[var(--color-line)]"
              style={{ height: 52 }}
            >
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#D6D6D0]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#D6D6D0]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#D6D6D0]" />
              </div>
              <div
                key={active.url}
                className="ml-4 font-mono text-[12px] text-[var(--color-ink-muted)] bg-white px-3.5 py-1.5 rounded-md border border-[var(--color-line)] url-fade"
              >
                {active.url}
              </div>
            </div>

            {/* Screenshot-Stapel — eigener Wrapper, der unterhalb der Bar beginnt und den Bereich sauber klammert */}
            <div
              className="absolute inset-x-0 bottom-0 overflow-hidden"
              style={{ top: 52 }}
            >
              {shots.map((s, i) => {
                const sp = slideProgress(i);
                const eased = 1 - Math.pow(1 - sp, 4);
                const y = (1 - eased) * 100;
                return (
                  <div
                    key={s.src}
                    className="absolute inset-0"
                    style={{
                      transform: `translateY(${y}%)`,
                      zIndex: i + 1,
                      boxShadow:
                        i > 0 && sp > 0 && sp < 1
                          ? "0 -30px 60px -10px rgba(10,10,15,0.2)"
                          : "none",
                    }}
                  >
                    <Image
                      src={s.src}
                      alt={`Screenshot: ${s.title}`}
                      fill
                      sizes="(min-width: 1400px) 1400px, 100vw"
                      priority={i === 0}
                      className="object-cover object-top"
                    />
                  </div>
                );
              })}
            </div>

          </div>
        </div>

        {/* Text-Block unter dem Frame — lebt mit im Sticky-Container */}
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="mx-auto max-w-[1400px]">
            {/* Zeile 1: Index + Progress-Dots — verbindet Label und Navigation in einer Achse */}
            <div className="flex items-center justify-between mb-3">
              <div
                key={`idx-${activeIndex}`}
                className="font-mono text-[11px] tracking-[0.18em] uppercase flex items-center gap-3 text-fade"
              >
                <span className="text-[var(--color-blue)]">
                  0{activeIndex + 1} / 0{N}
                </span>
                <span className="text-[var(--color-ink-muted)]">·</span>
                <span className="text-[var(--color-ink-muted)]">
                  {active.url.split(" / ")[1]}
                </span>
              </div>
              <div className="flex gap-1.5">
                {shots.map((_, i) => (
                  <span
                    key={i}
                    className="h-1 rounded-full transition-all duration-500"
                    style={{
                      width: i === activeIndex ? 28 : 8,
                      background:
                        i <= activeIndex
                          ? "var(--color-blue)"
                          : "rgba(10,10,15,0.12)",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Zeile 2: Titel — auf eigener Linie, volle Bühne */}
            <h3
              key={`title-${activeIndex}`}
              className="font-serif font-medium leading-[1.1] tracking-[-0.02em] text-[clamp(26px,3vw,40px)] mb-2 text-fade"
            >
              {active.title}
            </h3>

            {/* Zeile 3: Body — unter dem Titel, begrenzte Zeilenlänge für Lesbarkeit */}
            <p
              key={`body-${activeIndex}`}
              className="text-[var(--color-ink-soft)] leading-[1.55] text-[clamp(14px,1.05vw,16px)] max-w-[640px] text-fade"
            >
              {active.body}
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        .url-fade,
        .text-fade {
          animation: fadeIn 500ms cubic-bezier(0.25, 0.1, 0.25, 1) both;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .url-fade,
          .text-fade {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
